import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Crown, Plus, ShieldOff, UserCog, ClipboardList, Search, Trash2, KeyRound, Copy } from "lucide-react";
import StaffPayroll from "../components/StaffPayroll";

const ROLE_STYLES = {
    superadmin: "bg-yellow-100 text-yellow-900",
    admin: "bg-primary/10 text-primary",
    teacher: "bg-blue-100 text-blue-800",
    parent: "bg-secondary text-brand-ink",
};

const ROLE_LABELS = { parent: "Parent", teacher: "Teacher", teacher_volunteer: "Teacher/Volunteer", admin: "Admin", superadmin: "Super admin" };
const roleKey = (u) => (u.role === "teacher" && u.staff_type === "teacher_volunteer" ? "teacher_volunteer" : u.role);
const roleLabel = (k) => ROLE_LABELS[k] || k;

export default function SuperAdminConsole() {
    const { user } = useAuth();
    if (user?.role !== "superadmin") {
        return <div className="p-10 text-center text-brand-stone">Only super admins can access this page.</div>;
    }
    return (
        <div className="max-w-6xl mx-auto space-y-6" data-testid="superadmin-console">
            <div className="flex items-center gap-2">
                <Crown className="w-6 h-6 text-yellow-500" />
                <div>
                    <div className="overline text-brand-stone">Owner controls</div>
                    <h1 className="font-serif text-3xl">Super admin console</h1>
                </div>
            </div>
            <Tabs defaultValue="users">
                <TabsList className="mb-6 flex-wrap h-auto">
                    <TabsTrigger value="users" data-testid="sa-users-tab">Users & roles</TabsTrigger>
                    <TabsTrigger value="admins" data-testid="sa-admins-tab">Manage admins</TabsTrigger>
                    <TabsTrigger value="audit" data-testid="sa-audit-tab">Audit log</TabsTrigger>
                    <TabsTrigger value="payroll" data-testid="sa-payroll-tab">Staff pay</TabsTrigger>
                </TabsList>
                <TabsContent value="users"><UsersAndRoles selfId={user.id} isSuper /></TabsContent>
                <TabsContent value="admins"><ManageAdmins selfId={user.id} /></TabsContent>
                <TabsContent value="audit"><AuditLog /></TabsContent>
                <TabsContent value="payroll"><StaffPayroll /></TabsContent>
            </Tabs>
        </div>
    );
}

/** Role management — also embeddable from admin dashboard. */
export function UsersAndRoles({ selfId, isSuper = false }) {
    const [rows, setRows] = React.useState([]);
    const [q, setQ] = React.useState("");

    const load = React.useCallback(async () => {
        const { data } = await api.get("/admin/users");
        setRows(data);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const filtered = rows.filter(r =>
        !q || `${r.full_name} ${r.email} ${r.role}`.toLowerCase().includes(q.toLowerCase())
    );

    async function changeRole(u, role) {
        if (roleKey(u) === role) return;
        if (!window.confirm(`Change ${u.full_name} from ${roleLabel(roleKey(u))} to ${roleLabel(role)}?`)) return;
        try {
            await api.patch(`/admin/users/${u.id}/role`, { role });
            toast.success(`${u.full_name} is now a ${roleLabel(role)}`);
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Change failed"); }
    }

    async function resetTotp(u) {
        if (!window.confirm(`Reset 2FA for ${u.full_name}? They'll need to re-enrol.`)) return;
        try {
            await api.post(`/superadmin/users/${u.id}/reset-totp`);
            toast.success("2FA reset");
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    }

    const [resetResult, setResetResult] = React.useState(null); // { email, full_name, temp_password }

    async function resetPassword(u) {
        if (!window.confirm(`Generate a new temporary password for ${u.full_name}? Their current password will stop working.`)) return;
        try {
            const { data } = await api.post(`/superadmin/users/${u.id}/reset-password`);
            setResetResult(data);
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    }

    function copyTemp() {
        if (!resetResult?.temp_password) return;
        navigator.clipboard.writeText(resetResult.temp_password);
        toast.success("Temporary password copied");
    }

    return (
        <div>
            <div className="relative mb-4 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-stone" />
                <Input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} data-testid="users-search" className="pl-9" />
            </div>
            <div className="space-y-2">
                {filtered.map(u => (
                    <Card key={u.id}>
                        <CardContent className="p-4 flex items-center gap-4 flex-wrap" data-testid={`user-row-${u.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-medium">{u.full_name}</div>
                                    <Badge className={`${ROLE_STYLES[u.role] || ""} border-0`}>{roleLabel(roleKey(u))}</Badge>
                                </div>
                                <div className="text-xs text-brand-stone">{u.email}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Select value={roleKey(u)} onValueChange={v => changeRole(u, v)} disabled={u.id === selfId}>
                                    <SelectTrigger className="w-44" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="parent" data-testid={`role-parent-${u.id}`}>Parent</SelectItem>
                                        <SelectItem value="teacher" data-testid={`role-teacher-${u.id}`}>Teacher</SelectItem>
                                        <SelectItem value="teacher_volunteer" data-testid={`role-teacher-volunteer-${u.id}`}>Teacher/Volunteer</SelectItem>
                                        <SelectItem value="admin" data-testid={`role-admin-${u.id}`}>Admin</SelectItem>
                                        {isSuper && (
                                            <SelectItem value="superadmin" data-testid={`role-superadmin-${u.id}`}>Super admin</SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                                {isSuper && u.id !== selfId && (
                                    <>
                                        <Button variant="outline" size="sm" onClick={() => resetPassword(u)} data-testid={`reset-password-${u.id}`} className="gap-1.5">
                                            <KeyRound className="w-3.5 h-3.5" /> Reset password
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => resetTotp(u)} data-testid={`reset-totp-${u.id}`} className="gap-1.5">
                                            <ShieldOff className="w-3.5 h-3.5" /> Reset 2FA
                                        </Button>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {filtered.length === 0 && <div className="text-center py-10 text-brand-stone">No users match.</div>}
            </div>

            <Dialog open={!!resetResult} onOpenChange={v => { if (!v) setResetResult(null); }}>
                <DialogContent data-testid="password-reset-result">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Temporary password</DialogTitle></DialogHeader>
                    {resetResult && (
                        <div className="space-y-3">
                            <p className="text-sm text-brand-stone">
                                Share this password with <b>{resetResult.full_name}</b> ({resetResult.email}) through a secure channel.
                                They'll be asked to change it on next login.
                            </p>
                            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex items-center justify-between gap-2 font-mono text-base" data-testid="temp-password-value">
                                <span className="select-all">{resetResult.temp_password}</span>
                                <Button variant="outline" size="sm" onClick={copyTemp} data-testid="copy-temp-password" className="gap-1.5">
                                    <Copy className="w-3.5 h-3.5" /> Copy
                                </Button>
                            </div>
                            <p className="text-xs text-brand-stone italic">
                                This password is shown only once. Close this dialog and it's gone — generate a new one if the user doesn't receive it.
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ManageAdmins({ selfId }) {
    const [rows, setRows] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const load = React.useCallback(async () => {
        const { data } = await api.get("/admin/users", { params: { include_archived: true } });
        setRows(data.filter(u => u.role === "admin" || u.role === "superadmin"));
    }, []);
    React.useEffect(() => { load(); }, [load]);

    async function remove(u) {
        if (u.id === selfId) return toast.error("Can't delete yourself");
        if (!window.confirm(`Delete admin account ${u.email}?`)) return;
        try {
            await api.delete(`/superadmin/admins/${u.id}`);
            toast.success("Admin removed");
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-dash text-xl font-semibold">Admin accounts</h2>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="create-admin-btn" className="bg-primary hover:bg-primary/90 gap-2">
                            <Plus className="w-4 h-4" /> New admin
                        </Button>
                    </DialogTrigger>
                    <CreateAdminDialog onCreated={() => { setOpen(false); load(); }} />
                </Dialog>
            </div>
            <div className="space-y-2">
                {rows.map(u => (
                    <Card key={u.id}>
                        <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-medium">{u.full_name}</div>
                                    <Badge className={`${ROLE_STYLES[u.role]} border-0 capitalize`}>{u.role}</Badge>
                                    {u.archived && <Badge className="bg-rose-100 text-rose-800 border-0">Archived</Badge>}
                                    {u.totp_enabled && <Badge className="bg-emerald-100 text-emerald-800 border-0 gap-1">2FA on</Badge>}
                                </div>
                                <div className="text-xs text-brand-stone">{u.email}</div>
                            </div>
                            {u.id !== selfId && !u.archived && (
                                <Button variant="outline" size="sm" onClick={() => remove(u)} data-testid={`delete-admin-${u.id}`} className="gap-1.5 text-brand-stone hover:text-destructive">
                                    <Trash2 className="w-3.5 h-3.5" /> Remove
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}

function CreateAdminDialog({ onCreated }) {
    const [form, setForm] = React.useState({ email: "", full_name: "", phone: "", password: "", role: "admin" });
    const [busy, setBusy] = React.useState(false);
    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/superadmin/admins", form);
            toast.success("Admin created");
            onCreated();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    }
    return (
        <DialogContent>
            <DialogHeader><DialogTitle className="font-serif text-2xl">Create admin account</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
                <div><Label>Full name</Label><Input required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="new-admin-name" className="mt-1.5" /></div>
                <div><Label>Email</Label><Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="new-admin-email" className="mt-1.5" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="new-admin-phone" className="mt-1.5" /></div>
                <div><Label>Temporary password (they can change it later)</Label><Input required type="password" minLength={8} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="new-admin-password" className="mt-1.5" /></div>
                <div>
                    <Label>Role</Label>
                    <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="new-admin-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="superadmin">Super admin</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button type="submit" disabled={busy} data-testid="save-new-admin-btn" className="w-full bg-primary hover:bg-primary/90">
                    {busy ? "Creating…" : "Create admin"}
                </Button>
            </form>
        </DialogContent>
    );
}

function AuditLog() {
    const [rows, setRows] = React.useState([]);
    React.useEffect(() => { api.get("/superadmin/audit-log").then(r => setRows(r.data)); }, []);
    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <ClipboardList className="w-5 h-5 text-primary" />
                <h2 className="font-dash text-xl font-semibold">Recent admin actions</h2>
            </div>
            {rows.length === 0 ? (
                <div className="text-center py-10 text-brand-stone">No admin actions recorded yet.</div>
            ) : (
                <div className="space-y-2">
                    {rows.map(r => (
                        <Card key={r.id}>
                            <CardContent className="p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`audit-${r.id}`}>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge className={`${ROLE_STYLES[r.actor_role]} border-0 capitalize`}>{r.actor_role}</Badge>
                                        <span className="font-medium">{r.actor_email}</span>
                                        <span className="text-sm text-brand-stone">did</span>
                                        <Badge variant="secondary">{r.action}</Badge>
                                        {r.target_email && (<><span className="text-sm text-brand-stone">on</span><span className="text-sm font-medium">{r.target_email}</span></>)}
                                    </div>
                                    {r.meta && Object.keys(r.meta).length > 0 && (
                                        <pre className="text-[11px] text-brand-stone mt-1 whitespace-pre-wrap">{JSON.stringify(r.meta, null, 0)}</pre>
                                    )}
                                </div>
                                <div className="text-[11px] text-brand-stone whitespace-nowrap">{new Date(r.at).toLocaleString()}</div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
